import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ChatCircleDots, X, PaperPlaneTilt, Anchor, Sparkle, CheckCircle } from "@phosphor-icons/react";

const LS_SESSION = "epas_chat_session";
const LS_HISTORY = "epas_chat_history";

const WELCOME = {
  role: "assistant",
  content:
    "Welcome to EPAS. I'm the network concierge. Ask me anything about the curated freight forwarding network — or tell me about your company if you're exploring membership.",
  suggested: ["What is EPAS?", "Who can join?", "Apply for membership"],
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [sending, setSending] = useState(false);
  const [captured, setCaptured] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    const sid = localStorage.getItem(LS_SESSION);
    if (sid) setSessionId(sid);
    try {
      const stored = JSON.parse(localStorage.getItem(LS_HISTORY) || "null");
      if (Array.isArray(stored) && stored.length > 0) setMessages(stored);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    localStorage.setItem(LS_HISTORY, JSON.stringify(messages.slice(-40)));
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const body = (text ?? input).trim();
    if (!body || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: body }]);
    setSending(true);
    try {
      const { data } = await api.post("/chatbot/message", {
        session_id: sessionId || undefined,
        message: body,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(LS_SESSION, data.session_id);
      }
      setMessages((m) => [
        ...m,
        { role: "assistant", content: data.reply, suggested: data.suggested_replies || [] },
      ]);
      if (data.lead_captured) setCaptured(true);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry, I couldn't reach the server. Please try again in a moment.", suggested: [] },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const reset = () => {
    localStorage.removeItem(LS_SESSION);
    localStorage.removeItem(LS_HISTORY);
    setSessionId("");
    setMessages([WELCOME]);
    setCaptured(false);
  };

  return (
    <>
      {/* Trigger button */}
      {!open && (
        <button
          data-testid="chatbot-open-button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 left-5 z-[60] bg-navy text-white hover:bg-navy-800 shadow-2xl border border-white/10 px-4 py-3 flex items-center gap-2 group transition-all"
          aria-label="Open EPAS concierge"
        >
          <ChatCircleDots size={20} weight="duotone" className="text-ember" />
          <span className="text-sm font-medium tracking-tight">Ask the network concierge</span>
          <span className="ship-number text-[10px] uppercase tracking-widest text-ember/80 border-l border-white/20 pl-2 ml-1">AI</span>
        </button>
      )}

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-5 left-5 z-[60] w-[360px] sm:w-[400px] max-h-[80vh] bg-white border border-black/10 shadow-2xl flex flex-col overflow-hidden"
        >
          <header className="bg-navy text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <img src="/ep-air-sea-logo.png" alt="EP Air & Sea" className="h-6 w-auto logo-invert" />
              <div className="min-w-0 border-l border-white/15 pl-3">
                <div className="font-display font-bold text-sm leading-tight truncate">EPAS Concierge</div>
                <div className="text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1">
                  <Sparkle size={10} weight="fill" className="text-ember" /> AI · GPT-5-mini
                </div>
              </div>
            </div>
            <button
              data-testid="chatbot-close-button"
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white p-1"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </header>

          {captured && (
            <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2 text-xs text-emerald-700" data-testid="chatbot-lead-captured">
              <CheckCircle size={14} weight="fill" /> Your details have been saved. A human operator from Koper will follow up.
            </div>
          )}

          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#F8F8F8]" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <Message key={i} m={m} onQuick={(t) => send(t)} />
            ))}
            {sending && (
              <div className="flex gap-2 items-center text-navy/50 text-xs px-1" data-testid="chatbot-typing">
                <span className="w-1.5 h-1.5 bg-ember rounded-full animate-bounce" />
                <span className="w-1.5 h-1.5 bg-ember rounded-full animate-bounce [animation-delay:120ms]" />
                <span className="w-1.5 h-1.5 bg-ember rounded-full animate-bounce [animation-delay:240ms]" />
                Concierge is typing…
              </div>
            )}
          </div>

          <div className="border-t border-black/10 bg-white px-3 py-3">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="chatbot-input"
                value={input}
                rows={1}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask about membership, corridors, fit…"
                className="flex-1 resize-none border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy max-h-32"
              />
              <button
                data-testid="chatbot-send-button"
                onClick={() => send()}
                disabled={sending || !input.trim()}
                className="bg-navy text-white hover:bg-navy-800 px-3 py-2 disabled:opacity-40"
                aria-label="Send"
              >
                <PaperPlaneTilt size={16} weight="fill" />
              </button>
            </div>
            <div className="flex items-center justify-between mt-2 text-[10px] uppercase tracking-widest text-navy/40">
              <button onClick={reset} data-testid="chatbot-reset-button" className="hover:text-ember">Reset chat</button>
              <Link to="/apply" className="hover:text-ember" data-testid="chatbot-apply-link">Apply for membership →</Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const Message = ({ m, onQuick }) => {
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="bg-navy text-white text-sm px-3 py-2 max-w-[85%] whitespace-pre-wrap leading-relaxed">{m.content}</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start gap-2">
      <div className="bg-white border border-black/10 text-navy text-sm px-3 py-2 max-w-[90%] whitespace-pre-wrap leading-relaxed">{m.content}</div>
      {Array.isArray(m.suggested) && m.suggested.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {m.suggested.slice(0, 3).map((s, i) => (
            <button
              key={i}
              onClick={() => onQuick(s)}
              data-testid={`chatbot-suggestion-${i}`}
              className="text-[11px] uppercase tracking-widest border border-navy/20 text-navy/70 hover:bg-navy hover:text-white px-2 py-1 transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
